import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { MDXEditorMethods } from "@mdxeditor/editor";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { format, parseISO } from "date-fns";
import { ru } from "date-fns/locale";
import {
    faComment as commentOutline,
    faPaperPlane as paperPlane,
    faEdit as pencil,
    faTrash as trashBin,
    faUserCircle as userCircle,
} from '@fortawesome/free-solid-svg-icons';
import { Task } from "../../state/Task";
import { TaskComment } from "../../state/TaskComment";
import { useTaskCommentOperations, useTaskComments } from "../../storage/accessors";
import { useGravatar } from "../../hooks/useGravatar";
import { UserContext } from "../auth/auth";
import Icon from "../Icon";
import { ForwardRefEditor } from "../markdown/ForwardRefEditor";

interface Props {
    task: Task;
}

export default function TaskComments({ task }: Props) {
    const taskComments = useTaskCommentOperations();
    const comments = useTaskComments(task.uuid);
    const user = React.useContext(UserContext);
    const authorId = user.impersonate_code || user.email || "";
    const [deletingUuid, setDeletingUuid] = useState<string | null>(null);

    const isOwn = (comment: TaskComment) => comment.author === authorId;

    const confirmDelete = () => {
        if (deletingUuid) {
            taskComments.delete(deletingUuid);
        }
        setDeletingUuid(null);
    };

    return <>
        <h5 className="mb-3 mt-3">
            <Icon icon={commentOutline} className="me-2" />
            Комментарии{' '}
            <small className="text-secondary">({comments.length})</small>
        </h5>

        {comments.length === 0 && (
            <p className="text-muted"><small>Пока нет комментариев</small></p>
        )}

        <div className="d-flex flex-column gap-2">
            {comments.map(comment => (
                <CommentItem
                    key={comment.uuid}
                    comment={comment}
                    own={isOwn(comment)}
                    authorEmail={isOwn(comment) ? user.email : undefined}
                    onSave={(text) => taskComments.save(comment.uuid, { text })}
                    onDelete={() => setDeletingUuid(comment.uuid)}
                />
            ))}
        </div>

        <NewCommentForm
            onSubmit={(text) => taskComments.create({
                uuid: crypto.randomUUID(),
                task: task.uuid,
                date: new Date().toISOString(),
                author: authorId,
                text,
            })}
        />

        <Modal show={!!deletingUuid} onHide={() => setDeletingUuid(null)}>
            <Modal.Header closeButton>
                <Modal.Title>Удалить комментарий</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-0">Вы уверены, что хотите удалить комментарий?</p>
                <small className="text-muted">Это действие нельзя отменить.</small>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setDeletingUuid(null)}>Отмена</Button>
                <Button variant="danger" onClick={confirmDelete}>Удалить</Button>
            </Modal.Footer>
        </Modal>
    </>;
}

interface NewCommentFormProps {
    onSubmit: (text: string) => void;
}

// Отдельный компонент: ввод не поднимает состояние в TaskComments,
// иначе каждый символ перерисовывал бы весь список комментариев.
const NewCommentForm: React.FC<NewCommentFormProps> = ({ onSubmit }) => {
    const editor = useRef<MDXEditorMethods>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const submitRef = useRef<() => void>(() => { });

    const submit = () => {
        const text = editor.current?.getMarkdown()?.trim() || "";
        if (!text) return;
        onSubmit(text);
        editor.current?.setMarkdown("");
    };
    submitRef.current = submit;

    // Ctrl/Cmd+Enter отправляет комментарий. MDXEditor рендерит контент в Shadow DOM,
    // поэтому React-обработчики на обёртке ненадёжны — слушаем нативно (keydown composed,
    // всплывает до host-элемента).
    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                submitRef.current();
            }
        };
        el.addEventListener("keydown", handler, true);
        return () => el.removeEventListener("keydown", handler, true);
    }, []);

    return (
        <Card className="mt-3 bg-body-secondary">
            <Card.Body className="p-2">
                <Form.Label className="text-secondary"><small>Новый комментарий</small></Form.Label>
                <div className="lh-1" ref={wrapperRef}>
                    <ForwardRefEditor ref={editor} markdown="" className="lh-1" />
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <small className="text-secondary">Ctrl+Enter — отправить</small>
                    <Button size="sm" variant="success" onClick={submit}>
                        <Icon icon={paperPlane} className="me-1" /> Добавить
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

interface CommentItemProps {
    comment: TaskComment;
    own: boolean;
    authorEmail?: string;
    onSave: (text: string) => void;
    onDelete: () => void;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, own, authorEmail, onSave, onDelete }) => {
    const [editMode, setEditMode] = useState(false);
    const editor = useRef<MDXEditorMethods>(null);
    const gravatarUrl = useGravatar(authorEmail || "", 40);

    const dateLabel = useMemo(() => {
        try {
            return format(parseISO(comment.date), "dd MMMM yyyy, HH:mm", { locale: ru });
        } catch {
            return comment.date;
        }
    }, [comment.date]);

    const save = () => {
        const text = editor.current?.getMarkdown()?.trim() || "";
        if (!text) return;
        onSave(text);
        setEditMode(false);
    };

    return (
        <Card>
            <Card.Body className="py-2 px-3">
                <Row className="align-items-center g-2 mb-2">
                    <Col className="d-flex align-items-center gap-2">
                        {gravatarUrl
                            ? <img src={gravatarUrl} alt="" width={24} height={24} className="rounded-circle" />
                            : <Icon icon={userCircle} size="lg" className="text-secondary" />}
                        <small className="text-secondary">
                            {authorEmail || comment.author}
                        </small>
                        {own && <Badge bg="secondary">Вы</Badge>}
                    </Col>
                    <Col xs="auto" className="text-secondary">
                        <small>{dateLabel}</small>
                    </Col>
                    {own && !editMode && (
                        <Col xs="auto" className="d-flex gap-1">
                            <Button size="sm" variant="outline-primary" title="Редактировать" onClick={() => setEditMode(true)}>
                                <Icon icon={pencil} />
                            </Button>
                            <Button size="sm" variant="outline-danger" title="Удалить" onClick={onDelete}>
                                <Icon icon={trashBin} />
                            </Button>
                        </Col>
                    )}
                </Row>

                {editMode ? (
                    <>
                        <div className="lh-1">
                            <ForwardRefEditor ref={editor} markdown={comment.text || ""} className="lh-1" />
                        </div>
                        <div className="d-flex gap-2 mt-2">
                            <Button size="sm" variant="success" onClick={save}>Сохранить</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setEditMode(false)}>Отмена</Button>
                        </div>
                    </>
                ) : (
                    <div className="markdown-content" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                        <Markdown remarkPlugins={[remarkGfm]}>{comment.text}</Markdown>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};